/**
 * The element-pick crop: the picked page area of the current capture, as a
 * composer image attachment.
 *
 * Adapted from t3code's preview annotation flow
 * (`capturePreviewAnnotationScreenshot` and the pick handling in
 * `PreviewView.tsx`, MIT, pin `e3e7cc3f`). Forge delta: the pick is the
 * panel's capture-frame selection, so the crop happens client side from the
 * displayed screenshot instead of a desktop main-process element crop; the
 * stored target rect already carries capture-space pixels.
 *
 * @module browserAnnotationCrop
 */
import type { BrowserCommentRect, BrowserCommentTarget } from "@cmmd-forge/contracts";

import { buildBrowserScreenshotAttachmentMetadata } from "../BrowserPreviewPanel.capture";
import type { ComposerImageAttachment } from "~/composerDraftStore";
import { randomUUID } from "~/lib/utils";

/**
 * Intersects the picked rect with the capture's own bounds, so a selection
 * dragged past the image edge cannot ask the canvas for negative pixels.
 * Returns null when nothing of the selection is inside the image.
 */
export function clampBrowserPickRect(
  rect: BrowserCommentRect,
  bounds: { readonly width: number; readonly height: number },
): BrowserCommentRect | null {
  const x = Math.max(0, Math.min(rect.x, bounds.width));
  const y = Math.max(0, Math.min(rect.y, bounds.height));
  const width = Math.min(rect.x + rect.width, bounds.width) - x;
  const height = Math.min(rect.y + rect.height, bounds.height) - y;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { x, y, width, height };
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("The capture could not be decoded.")));
    image.src = dataUrl;
  });
}

async function dataUrlToFile(input: {
  readonly dataUrl: string;
  readonly name: string;
}): Promise<File> {
  const response = await fetch(input.dataUrl);
  const blob = await response.blob();
  return new File([blob], input.name, { type: blob.type || "image/png" });
}

/**
 * Crops the target's rect out of the capture and wraps it as a composer
 * image attachment. Null when the pick carried no region, or nothing of the
 * region lands inside the capture.
 */
export async function cropBrowserAnnotationScreenshot(input: {
  readonly dataUrl: string;
  readonly target: BrowserCommentTarget;
  readonly url: string | null;
  readonly nowMs: number;
}): Promise<ComposerImageAttachment | null> {
  const { target } = input;
  if (!target.rect) return null;
  const image = await loadImage(input.dataUrl);
  const rect = clampBrowserPickRect(target.rect, {
    width: image.naturalWidth,
    height: image.naturalHeight,
  });
  if (!rect) return null;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(rect.width);
  canvas.height = Math.round(rect.height);
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("This browser cannot crop the capture.");
  }
  context.drawImage(
    image,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  const croppedDataUrl = canvas.toDataURL("image/png");

  const metadata = buildBrowserScreenshotAttachmentMetadata({
    dataUrl: croppedDataUrl,
    url: input.url,
    nowMs: input.nowMs,
  });
  const file = await dataUrlToFile({ dataUrl: croppedDataUrl, name: metadata.name });
  if (file.size === 0) {
    throw new Error("The picked area cropped to an empty image.");
  }
  return {
    type: "image",
    id: randomUUID(),
    name: metadata.name,
    mimeType: metadata.mimeType,
    sizeBytes: file.size,
    previewUrl: croppedDataUrl,
    file,
  };
}
